// import "@aws-amplify/ui-react/styles.css";
import "./init-sentry";
import "./assets/styles/index.scss";
import "./i18n";

import { Amplify, Auth } from "aws-amplify";
import type { InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { rejectIfJsonPayloadTooLarge } from "helpers/jsonPayloadLimit";
import { t } from "i18next";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { toast } from "react-toastify";

import App from "./App";
import awsconfig from "./aws-exports.js";
import reportWebVitals from "./reportWebVitals";

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

awsconfig.oauth.redirectSignIn = window.location.origin + "/";
awsconfig.oauth.redirectSignOut = window.location.origin + "/";

/**
 * This is a hack to fix the issue with the Amplify library treating all incoming urls as OAuth responses.
 * https://github.com/aws-amplify/amplify-js/issues/9208#issuecomment-1309890756*
 */
// @ts-expect-error Private var
const _handleAuthResponse = Auth._handleAuthResponse.bind(Auth);
// @ts-expect-error Private var
Auth._handleAuthResponse = (url: string) => {
    const configuration = Auth.configure();
    if (url.includes("/callback")) return;
    return _handleAuthResponse(url);
};

Amplify.configure(awsconfig);

// After a new deployment, old hashed chunks no longer exist on the CDN.
// Vite fires `vite:preloadError` when a dynamic import 404s.
// We reload to pick up the fresh HTML + new chunk filenames.
// The guard is time-based (not a one-shot flag) so long-lived tabs still recover
// on later deployments, while a chunk that is genuinely missing can't cause a reload loop.
window.addEventListener("vite:preloadError", (event) => {
    const RELOAD_KEY = "vite_chunk_reload_at";
    const RELOAD_COOLDOWN_MS = 60_000;
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - lastReloadAt > RELOAD_COOLDOWN_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        // Prevents Vite from rethrowing: the page is reloading, the error is not actionable.
        event.preventDefault();
        window.location.reload();
    }
});

axios.interceptors.request.use(
    async function (config) {
        const tooLarge = rejectIfJsonPayloadTooLarge(config);
        if (tooLarge) return Promise.reject(tooLarge);

        const controller = new AbortController();
        const session = await Auth.currentSession();

        if (!session.isValid()) {
            toast.error(`${t("ServerError.RefreshTokenExpired")}`, { autoClose: 10000 });
            controller.abort();
        }

        config.signal = controller.signal;
        config.headers = {
            ...config.headers,
            Authorization: session.getAccessToken().getJwtToken(),
        } as InternalAxiosRequestConfig["headers"];
        return config;
    },
    function (error) {
        // Do something with request error
        return Promise.reject(error);
    },
);

root.render(
    import.meta.env.REACT_APP_STRICT_MODE === "true" ? (
        <StrictMode>
            <App />
        </StrictMode>
    ) : (
        <App />
    ),
);

reportWebVitals();
